import {
  Alert,
  Button,
  PageHeader,
  Skeleton,
  StatusBadge,
  Tab,
  TabList,
  TabPanel,
  Tabs,
} from '@catering/ui';
import { useQuery } from '@tanstack/react-query';
import { Link, getRouteApi } from '@tanstack/react-router';
import { FileText, History, Paperclip, Receipt } from 'lucide-react';
import type { JSX } from 'react';

import { ApiError } from '../../api/client';
import { useCurrentUser } from '../../auth/use-current-user';
import { hasPermission } from '../../auth/has-permission';
import { getContract } from '../../contracts/api';
import { formatCalendarDate } from '../../contracts/format-calendar-date';
import { searchAllCustomers } from '../../customers/api';
import { ContractConditionsTab } from './components/contract-conditions-tab';
import { ContractDocumentTab } from './components/contract-document-tab';
import { ContractPricesTab } from './components/contract-prices-tab';
import { ContractVersionHistoryTab } from './components/contract-version-history-tab';

export type ContractDetailTab = 'condiciones' | 'precios' | 'versiones' | 'documento';

const contractDetailRoute = getRouteApi('/protected/contracts/$contractId');

const TAB_LABELS: Record<ContractDetailTab, string> = {
  condiciones: 'Condiciones',
  precios: 'Lista de precios',
  versiones: 'Historial de versiones',
  documento: 'Documento',
};

const TAB_ICONS: Record<ContractDetailTab, typeof FileText> = {
  condiciones: FileText,
  precios: Receipt,
  versiones: History,
  documento: Paperclip,
};

/**
 * `contracts/spec.md` → "Pantallas de contrato en back-office". Tabs are
 * local state driven by the `tab` search param, not sub-routes
 * (`design.md` §10.2), same convention as `CustomerDetailPage`/
 * `MenuDetailPage`.
 */
export function ContractDetailPage(): JSX.Element {
  const { contractId } = contractDetailRoute.useParams();
  const { tab } = contractDetailRoute.useSearch();
  const navigate = contractDetailRoute.useNavigate();

  const query = useQuery({
    queryKey: ['contract', contractId],
    queryFn: () => getContract(contractId),
  });
  const currentUser = useCurrentUser();
  const customersQuery = useQuery({
    queryKey: ['customers-for-contracts'],
    queryFn: () => searchAllCustomers({ q: undefined, zone: undefined, status: undefined }),
  });

  function goToTab(next: ContractDetailTab): void {
    void navigate({ search: (prev) => ({ ...prev, tab: next }) });
  }

  if (query.isPending) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (query.isError) {
    if (query.error instanceof ApiError && query.error.status === 404) {
      return (
        <Alert tone="danger" title="Contrato no encontrado">
          <Link to="/contracts" className="underline">
            Volver al listado
          </Link>
        </Alert>
      );
    }

    return (
      <Alert tone="danger" title="No pudimos cargar el contrato">
        <div className="flex items-center justify-between gap-4">
          <span>Ocurrió un error al cargar. Intentá de nuevo.</span>
          <Button variant="secondary" size="sm" onClick={() => void query.refetch()}>
            Reintentar
          </Button>
        </div>
      </Alert>
    );
  }

  const contract = query.data;
  const customerName = customersQuery.data?.items.find(
    (customer) => customer.id === contract.customerId,
  )?.legalName;
  const validity =
    contract.endDate === null
      ? `Vigente desde el ${formatCalendarDate(contract.startDate)}`
      : `Vigencia ${formatCalendarDate(contract.startDate)} – ${formatCalendarDate(contract.endDate)}`;

  return (
    <section className="flex flex-col gap-6">
      <div className="flex flex-col gap-1">
        <PageHeader
          icon={FileText}
          title={contract.code}
          description={validity}
          actions={<StatusBadge domain="contract" status={contract.status} />}
        />
        {customerName === undefined ? null : (
          <p className="text-sm text-slate-500">
            <Link
              to="/customers/$customerId"
              params={{ customerId: contract.customerId }}
              className="underline"
            >
              {customerName}
            </Link>
          </p>
        )}
      </div>

      <Tabs value={tab} onValueChange={(value) => goToTab(value as ContractDetailTab)}>
        <TabList aria-label="Pestañas del contrato">
          {(Object.keys(TAB_LABELS) as ContractDetailTab[]).map((key) => (
            <Tab key={key} value={key} icon={TAB_ICONS[key]}>
              {TAB_LABELS[key]}
            </Tab>
          ))}
        </TabList>

        <TabPanel value="condiciones">
          <ContractConditionsTab
            contract={contract}
            customers={customersQuery.data?.items ?? []}
            currentUser={currentUser.data}
            onRefetch={() => void query.refetch()}
          />
        </TabPanel>

        <TabPanel value="precios">
          <ContractPricesTab
            contract={contract}
            canManage={hasPermission(currentUser.data, 'contract.price.manage')}
          />
        </TabPanel>

        <TabPanel value="versiones">
          <ContractVersionHistoryTab code={contract.code} />
        </TabPanel>

        <TabPanel value="documento">
          <ContractDocumentTab contract={contract} />
        </TabPanel>
      </Tabs>
    </section>
  );
}
